import React from 'react';
import { safeParseHardware, getHardwareDisplayName } from '../../utils/hardwareUtils';

function AdminDashboard({ user, supabaseClient }) {
    const [stats, setStats] = React.useState({
        totalUsers: 0,
        newUsersToday: 0,
        totalDonations: 0,
        pendingDonations: 0,
        totalPartners: 0,
        unreadMessages: 0
    });
    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [recentActivity, setRecentActivity] = React.useState([]);

    React.useEffect(() => {
        const fetchDashboardData = async () => {
            if (!supabaseClient) return;

            setIsLoading(true);
            setError(null);

            try {
                let users = [];
                let donations = [];
                let partners = [];
                let messages = [];

                try {
                    // Fetch user statistics
                    const { data, error } = await supabaseClient
                        .from('users')
                        .select('id, created_at');

                    if (!error) {
                        users = data || [];
                    } else {
                        console.error('Error fetching users:', error);
                    }
                } catch (err) {
                    console.error('Failed to fetch users:', err);
                }

                try {
                    // Fetch donation statistics
                    const { data, error } = await supabaseClient
                        .from('donations')  // Correct table name
                        .select('id, status, created_at');

                    if (!error) {
                        donations = data || [];
                    } else {
                        console.error('Error fetching donations:', error);
                    }
                } catch (err) {
                    console.error('Failed to fetch donations:', err);
                }

                try {
                    // Fetch partnership statistics
                    const { data, error } = await supabaseClient
                        .from('partnerships')  // Correct table name
                        .select('id');

                    if (!error) {
                        partners = data || [];
                    } else {
                        console.error('Error fetching partnerships:', error);
                        partners = [];
                    }
                } catch (err) {
                    console.error('Failed to fetch partnerships:', err);
                    partners = [];
                }

                try {
                    // First try "messages" table
                    const { data, error } = await supabaseClient
                        .from('messages')
                        .select('id, is_read');

                    if (error) {
                        // Fall back to "message" table
                        const altResponse = await supabaseClient
                            .from('message')
                            .select('id, is_read');
                            
                        if (!altResponse.error) {
                            messages = altResponse.data || [];
                        } else {
                            console.error('Error fetching messages:', altResponse.error);
                            messages = [];
                        }
                    } else {
                        messages = data || [];
                    }
                } catch (err) {
                    console.error('Failed to fetch messages:', err);
                    messages = [];
                }

                // Calculate statistics
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                const newUsersToday = users.filter(user => 
                    new Date(user.created_at) >= today
                ).length || 0;

                const pendingDonations = donations.filter(donation => 
                    donation.status === 'pending'
                ).length || 0;

                // Fetch recent activity
                const recentActivity = await fetchRecentActivity();

                // Update state with all stats
                setStats({
                    totalUsers: users.length || 0,
                    newUsersToday,
                    totalDonations: donations.length || 0,
                    pendingDonations,
                    totalPartners: partners.length || 0,
                    unreadMessages: messages.filter(m => m.is_read === false).length || 0
                });

                setRecentActivity(recentActivity);
                
            } catch (err) {
                console.error('Error fetching dashboard data:', err);
                setError('Failed to load dashboard data. Please try again.');
            } finally {
                setIsLoading(false);
            }
        };

        const fetchRecentActivity = async () => {
            const activities = [];
            const limit = 10;

            try {
                // Recent users
                try {
                    const { data: users } = await supabaseClient
                        .from('users')
                        .select('name, email, created_at')
                        .order('created_at', { ascending: false })
                        .limit(limit);

                    if (users && users.length > 0) {
                        users.forEach(user => {
                            activities.push({
                                type: 'user',
                                action: 'registered',
                                name: user.name || user.email?.split('@')[0] || 'User',
                                email: user.email || 'No Email',
                                timestamp: user.created_at,
                                icon: 'fa-user-plus'
                            });
                        });
                    }
                } catch (err) {
                    console.error('Error fetching user activity:', err);
                }

                // Recent donations
                try {
                    let donations = [];
                    const { data, error } = await supabaseClient
                        .from('donations')  // Correct table name
                        .select('id, user_id, hardware, status, created_at')
                        .order('created_at', { ascending: false })
                        .limit(limit);
                    
                    if (!error) {
                        donations = data || [];
                    }

                    if (donations.length > 0) {
                        donations.forEach(donation => {
                            // Parse hardware JSON to get a displayable type
                            let hardwareDisplay = 'hardware';
                            try {
                                if (donation.hardware) {
                                    // Use the utility function for safer parsing
                                    hardwareDisplay = getHardwareDisplayName(donation);
                                }
                            } catch (e) {
                                console.error('Error parsing hardware JSON in activity:', e);
                            }
                            
                            // Try to get user info if available
                            let userName = 'Anonymous';
                            let userEmail = 'N/A';
                            
                            activities.push({
                                type: 'donation',
                                action: 'submitted',
                                donationId: donation.id,
                                hardwareType: hardwareDisplay,
                                status: donation.status,
                                name: userName,
                                email: userEmail,
                                timestamp: donation.created_at,
                                icon: 'fa-gift'
                            });
                        });
                    }
                } catch (err) {
                    console.error('Error fetching donation activity:', err);
                }

                // Recent messages - Try both table names
                try {
                    let messages = [];
                    const { data, error } = await supabaseClient
                        .from('messages')
                        .select('id, name, email, message, created_at')
                        .order('created_at', { ascending: false })
                        .limit(limit);

                    if (error) {
                        // Try alternate table name
                        const altResponse = await supabaseClient
                            .from('message')
                            .select('id, name, email, message, created_at')
                            .order('created_at', { ascending: false })
                            .limit(limit);
                            
                        if (!altResponse.error) {
                            messages = altResponse.data || [];
                        }
                    } else {
                        messages = data || [];
                    }

                    if (messages && messages.length > 0) {
                        messages.forEach(message => {
                            activities.push({
                                type: 'message',
                                action: 'sent',
                                name: message.name || 'Anonymous',
                                email: message.email || 'No Email',
                                messagePreview: message.message ? 
                                    (message.message.substring(0, 50) + (message.message.length > 50 ? '...' : '')) : 
                                    'No message content',
                                timestamp: message.created_at,
                                icon: 'fa-envelope'
                            });
                        });
                    }
                } catch (err) {
                    console.error('Error fetching message activity:', err);
                }

                // Sort all activities by timestamp
                return activities
                    .filter(activity => activity.timestamp) // Filter out any activities without timestamps
                    .sort((a, b) => {
                        try {
                            return new Date(b.timestamp) - new Date(a.timestamp);
                        } catch (e) {
                            return 0;
                        }
                    })
                    .slice(0, limit);

            } catch (error) {
                console.error('Error fetching activity:', error);
                return [];
            }
        };

        fetchDashboardData();
    }, [supabaseClient]);

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        
        try {
            const options = { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            };
            return new Date(dateString).toLocaleDateString(undefined, options);
        } catch (e) {
            return 'Invalid date';
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 p-6 rounded-lg">
                <h2 className="text-xl font-bold text-red-700">Error</h2>
                <p className="text-red-600">{error}</p>
                <button 
                    onClick={() => window.location.reload()}
                    className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div>
            <h2 className="text-2xl font-bold mb-6">Overview</h2>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
                    <div className="flex justify-between items-center">
                        <div>
                            <p className="text-sm font-medium text-gray-500 mb-1">Total Users</p>
                            <h3 className="text-3xl font-bold text-gray-800">{stats.totalUsers}</h3>
                        </div>
                        <div className="bg-blue-100 p-3 rounded-full">
                            <i className="fas fa-users text-blue-600"></i>
                        </div>
                    </div>
                    <p className="text-sm text-green-600 mt-2">
                        <i className="fas fa-user-plus mr-1"></i>
                        {stats.newUsersToday} new today
                    </p>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
                    <div className="flex justify-between items-center">
                        <div>
                            <p className="text-sm font-medium text-gray-500 mb-1">Total Donations</p>
                            <h3 className="text-3xl font-bold text-gray-800">{stats.totalDonations}</h3>
                        </div>
                        <div className="bg-green-100 p-3 rounded-full">
                            <i className="fas fa-gift text-green-600"></i>
                        </div>
                    </div>
                    <p className="text-sm text-amber-600 mt-2">
                        <i className="fas fa-clock mr-1"></i>
                        {stats.pendingDonations} pending approval
                    </p>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
                    <div className="flex justify-between items-center">
                        <div>
                            <p className="text-sm font-medium text-gray-500 mb-1">Partnership Requests</p>
                            <h3 className="text-3xl font-bold text-gray-800">{stats.totalPartners}</h3>
                        </div>
                        <div className="bg-purple-100 p-3 rounded-full">
                            <i className="fas fa-handshake text-purple-600"></i>
                        </div>
                    </div>
                    <p className="text-sm text-purple-600 mt-2">
                        <i className="fas fa-building mr-1"></i>
                        Active partnerships
                    </p>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
                    <div className="flex justify-between items-center">
                        <div>
                            <p className="text-sm font-medium text-gray-500 mb-1">Messages</p>
                            <h3 className="text-3xl font-bold text-gray-800">{stats.unreadMessages}</h3>
                        </div>
                        <div className="bg-red-100 p-3 rounded-full">
                            <i className="fas fa-envelope text-red-600"></i>
                        </div>
                    </div>
                    <p className="text-sm text-red-600 mt-2">
                        <i className="fas fa-envelope-open mr-1"></i>
                        Unread messages
                    </p>
                </div>
            </div>
            
            {/* Recent Activity */}
            <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Recent Activity</h3>
                
                {recentActivity.length > 0 ? (
                    <div className="overflow-y-auto max-h-96">
                        <ul className="divide-y divide-gray-200">
                            {recentActivity.map((activity, index) => (
                                <li key={index} className="py-4">
                                    <div className="flex items-start">
                                        <div className={`p-2 rounded-full mr-4 ${
                                            activity.type === 'user' ? 'bg-blue-100 text-blue-600' :
                                            activity.type === 'donation' ? 'bg-green-100 text-green-600' :
                                            'bg-red-100 text-red-600'
                                        }`}>
                                            <i className={`fas ${activity.icon}`}></i>
                                        </div>
                                        <div className="flex-1">
                                            <p className="font-medium">
                                                {activity.name} {activity.action} {activity.type === 'donation' ? `a ${activity.hardwareType} donation` : activity.type}
                                                {activity.type === 'message' && (
                                                    <span className="text-sm font-normal text-gray-500 ml-2">
                                                        "{activity.messagePreview}"
                                                    </span>
                                                )}
                                            </p>
                                            <p className="text-sm text-gray-500">{activity.email}</p>
                                            <p className="text-xs text-gray-400">{formatDate(activity.timestamp)}</p>
                                        </div>
                                        {activity.status && (
                                            <span className={`px-2 py-1 text-xs rounded-full ${
                                                activity.status === 'completed' ? 'bg-green-100 text-green-800' :
                                                activity.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                'bg-red-100 text-red-800'
                                            }`}>
                                                {activity.status}
                                            </span>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                ) : (
                    <p className="text-gray-500">No recent activity found.</p>
                )}
            </div>
        </div>
    );
}

window.AdminDashboard = AdminDashboard;

export default AdminDashboard;
